import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'astroturf/react';

import { ModalElementContext } from 'js/design_system/Modal';

// These should eventually be updated to come from the design system
const DESKTOP_SIZE = 1140;
const TABLET_SIZE = 780;
const PHONE_SIZE = 430;

export type Props = {
  // The id of the title element in the modal.
  // This is optional but strongly recommended for improved accessibility
  ariaLabelledby?: string;
  // When true, the modal will fade and slide in,
  // and the backdrop will fade in
  animateIn?: boolean;
  /** For custom styling of the backdrop. */
  backdropClassName?: string;
  children?: React.ReactNode;
  /**
   * For custom styling of the modal container.
   * This prop can be passed manually, or using astroturf via `styled(Modal)`.
   */
  className?: string;
  /** Called when clicking the close button or the backdrop. */
  onClose: () => void;
  /** When true, show a close button in the top right corner of the modal. */
  showCloseButton?: boolean;
  /** Called when clicking the back button. */
  onBack?: () => void;
  /** When true, show a back button in the top left corner of the modal.  */
  showBackButton?: boolean;
  /** For passing HTML properties directly to the modal container */
  containerProps?: React.DetailsHTMLAttributes<HTMLDivElement>;
  /** When the open animation completes */
  onAnimationComplete?: () => void;
};

type State = {
  windowHeight: number;
};

const BACKDROP_IN_KEYFRAMES = [{ opacity: 0 }, { opacity: 1 }];

// For slide in animation
const CONTAINER_IN_KEYFRAMES = [
  { opacity: 0, transform: 'translateY(40px)' },
  { opacity: 1, transform: 'translateY(0)' },
];

const Backdrop = styled.div`
  background: rgba(0, 0, 0, 0.6);
  display: grid;
  left: 0;
  place-items: center;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 10;
  overflow-y: auto;
`;

const Container = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.5);
  padding: 32px;
  position: relative;
  max-width: 100%;

  @media (min-width: ${PHONE_SIZE}px) {
    max-width: 90%;
  }

  @media (min-width: ${TABLET_SIZE}px) {
    max-width: 80%;
  }

  @media (min-width: ${DESKTOP_SIZE}px) {
    max-width: 900px;
  }
`;

const CloseButton = styled.button`
  color: #777;
  cursor: pointer;
  font-size: 16px;
  position: absolute;
  top: 32px;
  right: 32px;
  background: none;
  border: none;
`;

const BackButton = styled.button`
  color: #777;
  cursor: pointer;
  font-size: 20px;
  position: absolute;
  top: 32px;
  left: 32px;
  background: none;
  border: none;
`;

/** @deprecated Use `Modal` from `js/design_system/Modal` instead. */
export class Modal extends React.Component<Props, State> {
  backdropRef: HTMLDivElement | null = null;
  containerRef: HTMLDivElement | null = null;
  focusedBefore: HTMLElement | null = null;
  overflowStyle = '';

  state = {
    windowHeight: window.innerHeight,
  };

  static defaultProps = {
    animateIn: true,
    showCloseButton: false,
    showBackButton: false,
    onBack: () => {},
  };

  componentDidMount() {
    const { animateIn, onAnimationComplete } = this.props;
    // Remember existing overflow property
    const { overflow } = window.getComputedStyle(document.body);
    this.overflowStyle = overflow;
    // Set overflow to hidden to disallow scrolling while modal is open
    document.body.style.overflow = 'hidden';

    if (!animateIn) return;
    this.containerRef
      ?.animate(CONTAINER_IN_KEYFRAMES, {
        duration: 320,
        easing: 'ease-out',
      })
      .finished.then(onAnimationComplete);
    this.backdropRef?.animate(BACKDROP_IN_KEYFRAMES, {
      duration: 320,
      easing: 'ease-out',
    });
    // Often activating this will cause focus to jump to an element deep in the html, but then the
    // user won't be able  to navigate to any of the in modal content without going back
    this.focusedBefore = document.activeElement as HTMLElement | null;
    this.focusedBefore?.blur();

    // Watch for resizing so that the backdrop height is always correct
    window.addEventListener('resize', this.watchWindowSize);
  }

  componentWillUnmount() {
    // Remove overflow: hidden from body when modal is closed
    document.body.style.overflow = this.overflowStyle;

    this.focusedBefore?.focus();

    window.removeEventListener('resize', this.watchWindowSize);
  }

  watchWindowSize = () => {
    this.setState({ windowHeight: window.innerHeight });
  };

  render() {
    const {
      ariaLabelledby,
      backdropClassName,
      children = null,
      className,
      onClose,
      showCloseButton,
      onBack,
      showBackButton,
      containerProps = {},
    } = this.props;
    const { windowHeight } = this.state;

    return (
      <ModalElementContext.Consumer>
        {(modalElement) =>
          ReactDOM.createPortal(
            <Backdrop
              style={{ height: windowHeight }}
              className={backdropClassName}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                setTimeout(onClose, 0);
              }}
              ref={(el: HTMLDivElement) => (this.backdropRef = el)}
            >
              <Container
                {...containerProps}
                aria-labelledby={ariaLabelledby}
                className={className}
                ref={(el: HTMLDivElement) => (this.containerRef = el)}
                role="modal"
              >
                {showBackButton && (
                  <BackButton aria-label="Back" onClick={onBack}>
                    {'<'} Back
                  </BackButton>
                )}
                {showCloseButton && (
                  <CloseButton
                    aria-label="Close"
                    onClick={() => setTimeout(onClose, 0)}
                  >
                    ×
                  </CloseButton>
                )}
                {children}
              </Container>
            </Backdrop>,
            document.getElementById('react-modal') ||
              modalElement?.current ||
              document.body,
          )
        }
      </ModalElementContext.Consumer>
    );
  }
}

export default Modal;
